/**
 * Bilíngue (inglês/árabe) com troca instantânea e direção do texto.
 *
 * A escolha fica no navegador do usuário; sem escolha, usa o idioma do próprio navegador.
 * Em árabe o documento passa a dir="rtl" e o CSS espelha o layout.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { en } from './en';
import { ar } from './ar';

export type Language = 'en' | 'ar';

const DICTIONARIES: Record<Language, Record<string, string>> = { en, ar };
const STORAGE_KEY = 'app_language';

function detectLanguage(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'ar') return saved;
  } catch {
    // Navegador sem acesso ao armazenamento: segue com o idioma detectado
  }
  return typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

interface LanguageContextValue {
  language: Language;
  dir: 'ltr' | 'rtl';
  isRtl: boolean;
  setLanguage: (language: Language) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectLanguage);

  useEffect(() => {
    const dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    document.documentElement.dir = dir;
  }, [language]);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Sem armazenamento a troca vale só para esta sessão
    }
  }, []);

  const t = useCallback((key: string, vars?: Record<string, string | number>) => {
    // Sem tradução no idioma escolhido, cai para o inglês e, por fim, para a própria chave
    let text = DICTIONARIES[language][key] ?? DICTIONARIES.en[key] ?? key;
    if (vars) {
      for (const [name, value] of Object.entries(vars)) {
        text = text.replace(new RegExp(`\\{${name}\\}`, 'g'), String(value));
      }
    }
    return text;
  }, [language]);

  const value = useMemo<LanguageContextValue>(() => ({
    language,
    dir: language === 'ar' ? 'rtl' : 'ltr',
    isRtl: language === 'ar',
    setLanguage,
    t,
  }), [language, setLanguage, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used inside LanguageProvider');
  }
  return context;
}

/** Atalho para quem só precisa traduzir */
export function useT() {
  return useLanguage().t;
}
